<template>
  <div id="client-list" class="container item-list pb-64">
    <v-list-header
      :title="$t('client.clients')"
      icon="group"
      @open-filters-offcanvas="openOffCanvas('the-client-filters-offcanvas')"
      @emit-close-off-canvas="closeOffCanvas"
      @reload-clients="loadClients"
    >
      <template #input-search>
        <v-input-text-search
          id="client-search"
          v-model="mix"
          :placeholder="$t('general.shared.search')"
          autocomplete="off"
          class="w-100 me-24"
          input-class="h-auto py-8"
          input-group-text-class="py-8 px-16"
          type="text"
          @update:modelValue="mix = $event; search()"
        />
      </template>
    </v-list-header>

    <div class="items-content">
      <div class="filters-container mb-24">
        <v-filters
          v-if="!!filterOptions"
          v-model="filterOptions"
          @remove-filter="removeFilter"
        />
      </div>

      <div class="table-responsive d-none d-md-flex">
        <v-table>
          <v-table-header>
            <tr>
              <th>{{ $t('general.shared.code') }}</th>

              <th>
                <v-button
                  :class="{ 'sort-active': order.key === 'name' }"
                  class="btn-order text-md fw-light shadow-none p-0 pb-4"
                  variant="icon"
                  @click="toggleOrder('name')"
                >
                  {{ $t('general.shared.name') }}
                  <v-icon
                    :class="{ 'sort-icon-transition': order.key === 'name' && order.order === 'DESC' }"
                    class="ms-8"
                    icon="chevron-down"
                    size="xs"/>
                </v-button>
              </th>

              <th>
                <v-button
                  :class="{ 'sort-active': order.key === 'surname' }"
                  class="btn-order text-md fw-light shadow-none p-0 pb-4"
                  variant="icon"
                  @click="toggleOrder('surname')"
                >
                  {{ $t('general.shared.surname') }}
                  <v-icon
                    :class="{ 'sort-icon-transition': order.key === 'surname' && order.order === 'DESC' }"
                    class="ms-8"
                    icon="chevron-down"
                    size="xs"/>
                </v-button>
              </th>

              <th>{{ $t('general.shared.email') }}</th>

              <th>
                <v-button
                  :class="{ 'sort-active': order.key === 'source' }"
                  class="btn-order text-md fw-light shadow-none p-0 pb-4"
                  variant="icon"
                  @click="toggleOrder('source')"
                >
                  {{ $t('general.shared.source') }}
                  <v-icon
                    :class="{ 'sort-icon-transition': order.key === 'source' && order.order === 'DESC' }"
                    class="ms-8"
                    icon="chevron-down"
                    size="xs"/>
                </v-button>
              </th>

              <th class="fit"/>
            </tr>
          </v-table-header>

          <v-table-body class="fw-medium">
            <tr v-if="!loaded">
              <td class="position-relative border-0 py-96" colspan="2222">
                <v-loader/>
              </td>
            </tr>

            <template v-else-if="!!clients && clients.length > 0">
              <tr v-for="client in clients" :key="client.id">
                <td class="d-flex">
                  <div class="image-container rounded-sm bg-tertiary me-12">
                    {{ client.name.charAt(0) }}
                  </div>

                  <a
                    class="py-12"
                    href="#"
                    @click.prevent="showClientDetails(client)"
                  >
                    {{ client.code }}
                  </a>
                </td>

                <td>
                  <a
                    class="py-12"
                    href="#"
                    @click.prevent="showClientDetails(client)"
                  >
                    {{ client.name }}
                  </a>
                </td>

                <td>{{ client.surname }}</td>
                <td>{{ client.email }}</td>
                <td>{{ client.source }}</td>

                <td class="fit text-end">
                  <v-button
                    class="btn-view rounded-pill bg-secondary"
                    size="xs"
                    variant="icon"
                    @click="showClientDetails(client)"
                  >
                    <v-icon icon="arrow-right"/>
                  </v-button>
                </td>
              </tr>
            </template>

            <template v-else>
              <tr>
                <td class="rounded-xs py-16" colspan="2222">
                  {{ $t('client.noClients') }}
                </td>
              </tr>
            </template>
          </v-table-body>
        </v-table>
      </div>

      <div class="item-cards d-block d-md-none">
        <div v-if="!loaded" class="position-relative py-96">
          <v-loader/>
        </div>

        <template v-else-if="!!clients && clients.length > 0">
          <div v-for="client in clients" :key="client.id" class="item-card border rounded-xs p-8 mb-12 text-sm">
            <div class="d-flex align-items-center mb-4">
              <div class="image-container rounded-sm bg-tertiary me-12">
                {{ client.name.charAt(0) }}
              </div>

              <div>
                <div class="fw-medium">{{ client.name }} {{ client.surname }}</div>
                <div class="text-xs">{{ client.email }}</div>
              </div>
            </div>

            <div class="d-flex justify-content-between align-items-center">
              <div class="text-xs">{{ client.source }}</div>

              <v-button
                class="btn-view rounded-pill bg-secondary"
                size="xs"
                variant="icon"
                @click="showClientDetails(client)"
              >
                <v-icon class="fw-semi" icon="arrow-right" size="xs"/>
              </v-button>
            </div>
          </div>
        </template>

        <div v-else>
          {{ $t('client.noClients') }}
        </div>
      </div>

      <v-pagination
        v-if="!!clients && clients.length > 0"
        :meta="pagination.meta"
        :pagination-links="pagination.links"
        class="mt-32"
        @page-changed="changePage($event); handlePageChangedEvent($event)"
        @per-page-changed="perPage = $event; doSearch()"
      />
    </div>

    <the-client-filters-offcanvas
      v-if="offCanvasToShow === 'the-client-filters-offcanvas'"
      v-model:source="source"
      v-model:status="status"
      @closed="closeOffCanvas"
      @selected="doSearch"
    />

    <the-client-details-offcanvas
      v-if="offCanvasToShow === 'the-client-details-offcanvas'"
      :client-data="client"
      :is-archived="status === 'archived'"
      @closed="closeOffCanvas(); cleanCurrentClient()"
      @reload-clients="loadClients"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { VInputTextSearch } from '@uniqoders/form';
import qs from 'qs';
import querystring from 'query-string';
import { mapState } from 'pinia';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import VButton from '@/components/vendor/basic/button/VButton.vue';
import VTable from '@/components/vendor/basic/table/VTable.vue';
import VTableHeader from '@/components/vendor/basic/table/VTableHeader.vue';
import VTableBody from '@/components/vendor/basic/table/VTableBody.vue';
import VLoader from '@/components/vendor/loader/VLoader.vue';
import api from '@/api';
import withTimeout from '@/helpers/timeOut';
import useOffCanvasUtils from '@/helpers/OffCanvasUtils';
import VListHeader from '@/components/shared/VListHeader.vue';
import VPagination from '@/components/vendor/basic/table/VPagination.vue';
import usePagination from '@/helpers/pagination';
import VFilters from '@/components/vendor/filters/VFilters.vue';
import TheClientFiltersOffcanvas from '@/components/client/TheClientFiltersOffcanvas.vue';
import TheClientDetailsOffcanvas from '@/components/client/TheClientDetailsOffcanvas.vue';
import Client from '@/api/objects/Client';
import { findConceptByKey } from '@/helpers/ConceptHelper';
import { useAuthStore } from '@/stores/auth';

export default defineComponent({
  name: 'TheClients',
  components: {
    TheClientDetailsOffcanvas,
    TheClientFiltersOffcanvas,
    VFilters,
    VPagination,
    VListHeader,
    VLoader,
    VTableBody,
    VTableHeader,
    VTable,
    VButton,
    VInputTextSearch,
    VIcon,
  },
  setup() {
    return {
      ...useOffCanvasUtils(),
      ...usePagination(),
    };
  },
  data() {
    return {
      clients: [] as Client[],
      client: null as null | Client,
      status: 'active' as string,
      mix: '' as string,
      source: '' as string,
      filters: [
        {
          variable: 'status',
          key: 'status',
          labelAux: 'general.shared.filterStatus',
          conceptName: 'app.states',
          multiple: false,
          isConcept: true,
        },
        {
          variable: 'mix',
          key: 'mix',
          labelAux: 'general.shared.filterSearch',
          multiple: false,
          isConcept: false,
        },
        {
          variable: 'source',
          key: 'source',
          labelAux: 'general.shared.filterSource',
          conceptName: 'client.source_type',
          multiple: false,
          isConcept: true,
        },
      ] as Record<string, string | boolean>[],
      order: {
        key: '' as string,
        order: '' as string,
      } as Record<string, any>,
      perPage: 15 as number,
      page: 1 as number,
      timers: {
        name: null as any,
      },
      loaded: false as boolean,
      filterOptions: [] as Record<string, any>[],
    };
  },
  computed: {
    ...mapState(useAuthStore, ['agency']),
  },
  async created() {
    await this.setQueryParametersAsFilters();
    await this.doSearch();
  },
  methods: {
    search() {
      clearTimeout(this.timers.name);

      this.timers.name = withTimeout(async () => {
        await this.doSearch();
      }, this.timers.name, 1000);
    },
    /**
     * Detects any query parameter in the url and loads it in the filters (if available)
     *
     * @returns {Promise<void>}
     */
    async setQueryParametersAsFilters() {
      const { query }: any = this.$route;

      const {
        filters,
        order,
        perPage,
        status,
        page,
      }: any = qs.parse(query);

      if (!!filters) {
        this.filters.forEach((filter: Record<string, any>) => {
          if (!!filters[filter.key]) {
            this[filter.variable] = filters[filter.key];
          }
        });
      }

      if (!!order) {
        this.order = order;
      }

      const pageNumber = parseInt(page, 10);

      if (!Number.isNaN(pageNumber)) {
        this.page = pageNumber;
      }

      const perPageNumber = parseInt(perPage, 10);

      if (!Number.isNaN(perPageNumber)) {
        this.perPage = perPageNumber;
      }

      if (!!status) {
        this.status = status;
      }
    },
    /**
     * Sets the local filters as url query parameters.
     */
    async setFiltersAsQueryParameters() {
      const filters: any = {};

      this.filters.forEach((filter: Record<string, any>) => {
        if (!!this[filter.variable]) {
          filters[filter.key] = this[filter.variable];
        }
      });

      const queryObject: any = {
        filters,
        perPage: this.perPage,
        status: this.status,
        page: this.page,
      };

      if (!!this.order && !!this.order.key) {
        queryObject.order = this.order;
      }

      await this.setFilters();

      let query: any = qs.stringify(queryObject, { encode: false });
      query = querystring.parse(query);

      this.$router.push({
        name: 'client.index',
        query,
      });
    },
    async doSearch() {
      await this.setFiltersAsQueryParameters();
      await this.loadClients();
    },
    async loadClients(page?: number) {
      try {
        this.loaded = false;

        const filters: any = {};

        this.filters.forEach((filter: Record<string, any>) => {
          if (!!this[filter.variable]) {
            filters[filter.key] = this[filter.variable];
          }
        });

        const query: any = {
          filters,
          order: this.order,
          perPage: this.perPage,
          status: this.status,
          page: !!page ? page : this.page,
        };

        if (!!this.agency) {
          const response = await api.client.allPaginated(this.agency.slug, query);

          this.setResponse(response);

          this.clients = response.data;
        }
      } catch (e: any) {
        console.error(e);

        this.$toast.error(e.response.data.message);
      } finally {
        this.loaded = true;
      }
    },
    async showClientDetails(client: Client) {
      this.client = client;

      if (this.offCanvasToShow !== 'the-client-details-offcanvas') {
        this.openOffCanvas('the-client-details-offcanvas');
      }
    },
    cleanCurrentClient() {
      this.client = null;
    },
    async handlePageChangedEvent(page: number) {
      await this.loadClients(page);
    },
    toggleOrder(field: string) {
      const { order } = this.order;

      if (!order) {
        this.order.order = 'ASC';
      } else {
        this.order.order = order === 'ASC' ? 'DESC' : 'ASC';
      }

      this.order.key = field;

      this.search();
    },
    setFilters() {
      this.filterOptions = this.filters.flatMap((filter: Record<string, any>) => {
        const {
          key,
          multiple,
          isConcept,
          labelAux,
          conceptName,
          variable,
        } = filter;

        const options = multiple ? this[variable] : [this[variable]];

        return options.map((option: any) => {
          const value = isConcept ? findConceptByKey(`${conceptName}`, option) : option;
          const label = this.$t(`${labelAux}`, { value: !!conceptName ? value.name : value });

          return {
            key,
            value: !!conceptName ? value.key : value,
            label,
          };
        });
      });
    },
    removeFilter(filter: Record<string, string>) {
      this.filters.forEach((filterAux: Record<string, string | boolean>) => {
        if (filterAux.key === filter.key) {
          if (!filterAux.multiple) {
            this[filterAux.variable] = '';
          } else {
            this[filterAux.variable] = this[filterAux.variable].filter((option: string) => option !== filter.value);
          }
        }
      });

      if (filter.key === 'status') {
        this.status = 'active';
      }

      this.doSearch();
    },
  },
});
</script>
